import { useDocument } from "@/composables/useFirestore";
import { db } from "@/firebase";
import type { Account } from "@aircap/common/account";
import type { BridgeAccount } from "@aircap/common/bridge";
import type { GclAccount } from "@aircap/common/gcl";
import type { PowensAccount } from "@aircap/common/powens";
import { collection, doc } from "firebase/firestore";
import { computed, type Ref } from "vue";

export const useBankAccount = (accountId: Ref<string>) => {
  const accountSource = computed(() => doc(collection<Account>(db, "accounts"), accountId.value));
  const { data: account, pending: loadingAccount } = useDocument<Account>(accountSource);

  const bridgeAccountSource = computed(() =>
    account.value?.accountType === "bridge" && account.value?.bankAccountId
      ? doc<BridgeAccount>(
          db,
          `accounts/${accountId.value}/bridgeAccounts/${account.value.bankAccountId}`,
        )
      : null,
  );

  const { data: bridgeAccount } = useDocument<BridgeAccount>(bridgeAccountSource);

  const powensAccountSource = computed(() =>
    account.value?.accountType === "powens" && account.value?.bankAccountId
      ? doc<PowensAccount>(
          db,
          `accounts/${accountId.value}/powensAccounts/${account.value.bankAccountId}`,
        )
      : null,
  );

  const gclAccountSource = computed(() =>
    account.value?.accountType === "gcl" && account.value?.bankAccountId
      ? doc<GclAccount>(
          db,
          `accounts/${accountId.value}/gclAccounts/${account.value.bankAccountId}`,
        )
      : null,
  );

  const { data: powensAccount } = useDocument<PowensAccount>(powensAccountSource);
  const accountType = computed(() => account.value?.accountType);
  const swanAccount = computed(() => account.value?.swan_account);
  const treezorAccount = computed(() => ({
    company: account.value?.treezorCompany,
    wallet: account.value?.treezorWallet,
  }));
  const { data: gclAccount } = useDocument<GclAccount>(gclAccountSource);

  return {
    account,
    loadingAccount,
    accountType,
    bridgeAccount,
    powensAccount,
    swanAccount,
    gclAccount,
    treezorAccount,
  };
};
